import React, {useEffect, useState} from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import { Box } from "@mui/material"
import { DATE_FORMAT } from "src/utils/constants"
import FormActions from "../../components/FormActions"
import ValidationParametersSection from "./validation-parameters-section"
import ValidationResultsSection from "./validation-results-section"
import ValidationMemoriesSection from "./validation-memories-section"
import {
    CALCULATION_STATUS,
    getValidationViewModel,
    validationContextPropType,
    validationSnapshotPropType,
    validationViewModelPropType,
} from "./validation-contract"
import {
    getDecimalValidationMessage,
    isValidationParametersReady,
} from "./validation-utils"
import DuplaConfirmacao from "src/v2/components/DuplaConfirmacao/DuplaConfirmacao"

const ValidationPage = ({
    onBack,
    onCalculate,
    onExport,
    onSaveEmission,
    onSaveValidation,
    enableEmissionSave = false,
    viewModel = {},
    validationContext = {},
    calculationStatus = CALCULATION_STATUS.IDLE,
    parameters,
    onParametersChange,
    savedViewModel,
    isLoadingValidation = false,
    isSavingEmission = false,
    isSavingValidation = false,
    referenceDateError = false,
    validationSnapshot,
}) => {
    const [showSaveEmissionConfirmation, setShowSaveEmissionConfirmation] = useState(false)

    const isSaving = isSavingEmission || isSavingValidation

    const isCalculationComplete =
        calculationStatus === CALCULATION_STATUS.SUCCESS

    const hasSavedValidation =
        calculationStatus === CALCULATION_STATUS.IDLE
        && savedViewModel != null

    const hasResults = !isLoadingValidation
        && (isCalculationComplete || hasSavedValidation)

    const { results, premios, memories } = getValidationViewModel(
        hasResults
            ? (isCalculationComplete ? viewModel : savedViewModel)
            : undefined
    )

    const decimalErrors = {
        puparReferencia: getDecimalValidationMessage(parameters.puparReferencia, {allowNegative: false}),
        pumtmReferencia: getDecimalValidationMessage(parameters.pumtmReferencia, {allowNegative: false}),
        spreadCredito: getDecimalValidationMessage(parameters.spreadCredito),
    }

    const canCalculate = Boolean(onCalculate)
        && !isLoadingValidation
        && !isSaving
        && calculationStatus !== CALCULATION_STATUS.LOADING
        && isValidationParametersReady(parameters)

    const changeParameter = (field) => (event) => {
        onParametersChange((current) => ({
            ...current,
            [field]: event.target.value,
        }))
    }

    const changeReferenceDate = (value) => {
        const selectedDate = value ? dayjs(value) : null

        onParametersChange((current) => ({
            ...current,
            dataReferencia: selectedDate?.isValid()
                ? selectedDate.format(DATE_FORMAT)
                : "",
        }))
    }

    const calculationPayload = validationSnapshot
        ? {parameters, validationContext, snapshot: validationSnapshot}
        : {parameters, validationContext}

    const validationResultPayload = {
        ...calculationPayload,
        parameters: viewModel.parameters ?? parameters,
        results,
        premios,
        memories,
    }

    const emissionCharacteristicsPayload = {
        validationContext,
        spreadEmissao: results.spreadEmissao,
        prazoMedioEmissao: results.prazoMedioEmissao,
    }

    const handleSaveEmission = enableEmissionSave
        && hasResults
        && isCalculationComplete
        && results.spreadEmissao != null
        && results.prazoMedioEmissao != null
        && !isSaving
        && onSaveEmission
        ? () => setShowSaveEmissionConfirmation(true)
        : undefined

    const handleCancelSaveEmission = () => {
        if (isSaving) return
        setShowSaveEmissionConfirmation(false)
    }

    const handleConfirmSaveEmission = async () => {
        if (isSaving) return
        if (!handleSaveEmission) {
            setShowSaveEmissionConfirmation(false)
            return
        }

        try {
            await onSaveEmission(emissionCharacteristicsPayload)
        } finally {
            setShowSaveEmissionConfirmation(false)
        }
    }

    useEffect(() => {
        setShowSaveEmissionConfirmation(false)
    }, [viewModel, calculationStatus])

    const handleSaveValidation = hasResults && isCalculationComplete
        && viewModel.parameters && !isSaving && onSaveValidation
        ? () => onSaveValidation(validationResultPayload)
        : undefined

    const handleExport = hasResults && isCalculationComplete && !isSaving && onExport
        ? () => onExport(validationResultPayload)
        : undefined

    return (
        <Box sx={{ pb: 16, px: { xxxs: 2, md: 3, lg: 0 } }}>
            <Box
                component="fieldset"
                disabled={isLoadingValidation || isSaving}
                sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
            >
                <ValidationParametersSection
                    parameters={parameters}
                    referenceDateError={referenceDateError}
                    decimalErrors={decimalErrors}
                    onParameterChange={changeParameter}
                    onReferenceDateChange={changeReferenceDate}
                    onCalculate={
                        canCalculate
                            ? () => onCalculate(calculationPayload)
                            : undefined
                    }
                    canCalculate={canCalculate}
                    isCalculating={calculationStatus === CALCULATION_STATUS.LOADING}
                />
            </Box>
            <ValidationResultsSection
                calculationStatus={calculationStatus}
                hasResults={hasResults}
                isLoadingValidation={isLoadingValidation}
                results={results}
                premios={premios}
                onExport={handleExport}
                onSaveEmission={handleSaveEmission}
                onSaveValidation={handleSaveValidation}
                isSavingValidation={isSavingValidation}
                isSavingEmission={isSavingEmission}
            />
            <ValidationMemoriesSection memories={memories} premios={premios} />

            <FormActions
                showBack
                onBack={onBack}
                showDelete={false}
                showSave={false}
                showPublish={false}
            />

            <DuplaConfirmacao
                open={showSaveEmissionConfirmation}
                title="Salvar spread e prazo médio da emissão"
                description="Os valores atuais de spread e prazo médio do cadastro serão substituídos pelos valores exibidos no resultado. Deseja continuar?"
                cancel={handleCancelSaveEmission}
                confirm={handleConfirmSaveEmission}
                confirmTextButton={isSavingEmission ? "SALVANDO..." : "SALVAR"}
            />
        </Box>
    )
}

ValidationPage.propTypes = {
    onBack: PropTypes.func.isRequired,
    onCalculate: PropTypes.func,
    onExport: PropTypes.func,
    onSaveEmission: PropTypes.func,
    onSaveValidation: PropTypes.func,
    enableEmissionSave: PropTypes.bool,
    isSavingEmission: PropTypes.bool,
    isSavingValidation: PropTypes.bool,
    calculationStatus: PropTypes.oneOf(Object.values(CALCULATION_STATUS)),
    viewModel: validationViewModelPropType,
    validationContext: validationContextPropType,
    validationSnapshot: validationSnapshotPropType,
    parameters: PropTypes.shape({
        dataReferencia: PropTypes.string.isRequired,
        puparReferencia: PropTypes.string.isRequired,
        pumtmReferencia: PropTypes.string.isRequired,
        spreadCredito: PropTypes.string.isRequired,
    }).isRequired,
    onParametersChange: PropTypes.func.isRequired,
    savedViewModel: validationViewModelPropType,
    isLoadingValidation: PropTypes.bool,
    referenceDateError: PropTypes.bool,
}

export default ValidationPage
